/** @file Structural shape of a puppet document (design 4.1, 17.7, 17.8); references: validatePuppet. */
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkEnum } from '@zilo/pose3d-format/validate/fields/checkEnum';
import { checkId } from '@zilo/pose3d-format/validate/fields/checkId';
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { checkRecord } from '@zilo/pose3d-format/validate/fields/checkRecord';
import { checkShape } from '@zilo/pose3d-format/validate/fields/checkShape';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { checkTilePx } from '@zilo/pose3d-format/validate/fields/checkTilePx';
import { checkTuple } from '@zilo/pose3d-format/validate/fields/checkTuple';
import type { FieldCheck, Shape } from '@zilo/pose3d-format/validate/fields/fieldCheck';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';
import { ATTACHMENT_WHEN_SHAPE } from '@zilo/pose3d-format/validate/shapes/attachmentWhenShape';
import { checkDrawable } from '@zilo/pose3d-format/validate/shapes/checkDrawable';
import { NODE_SHAPE } from '@zilo/pose3d-format/validate/shapes/nodeShape';
import { SECTOR_TABLE_SHAPE } from '@zilo/pose3d-format/validate/shapes/sectorTableShape';

const name = checkString(NAME_PATTERN);
const steps = checkNumber({ int: true, min: 1 });
const facingSteps: FieldCheck = (value, path) => { if (value !== 'free') steps(value, path); };
const positive = checkNumber({ above: 0 });

/** Shape of `Attachment` (design 17.8, R1). */
const ATTACHMENT = checkShape({
  required: { id: name, source: checkId('pup'), nodeMap: checkRecord(name) },
  optional: { weaponGroup: name, when: checkShape(ATTACHMENT_WHEN_SHAPE), driver: name },
});

/** Shape of `Puppet`. */
export const PUPPET_SHAPE: Shape = {
  required: {
    format: checkEnum(['pose3d/puppet@1']),
    id: checkId('pup'),
    name: checkString(),
    profile: name,
    facing: checkShape({ required: { steps: facingSteps, turnRate: positive } }),
    nodes: checkRecord(checkShape(NODE_SHAPE)),
    sockets: checkRecord(name),
    drawables: checkArray(checkDrawable),
    variants: checkArray(name),
    sheets: checkRecord(checkShape({ required: { image: checkString(), size: checkTuple(2, { int: true, min: 1 }) } })),
    bounds: checkTuple(3, { above: 0 }),
  },
  optional: {
    profileOverrides: checkShape({ required: {}, optional: { zScaleRotate: positive, zScaleView: positive, tilePx: checkTilePx } }),
    sectorTables: checkRecord(checkShape(SECTOR_TABLE_SHAPE)),
    attachments: checkArray(ATTACHMENT),
  },
};
